import React from 'react';

/**
 * Opens an address in Google Maps as a new top-level tab. Looks like an
 * anchor visually (so users still get the right-click "Open in new tab"
 * affordance), but we override the default click with `window.open` so
 * iframe-hosted contexts (e.g. the Emergent preview) don't get an
 * `ERR_BLOCKED_BY_RESPONSE` from Google's CSP. The `href` stays
 * populated so middle-click and right-click still work in normal
 * browser contexts.
 */
const MapsLink = ({ address, children, className = '', testid }) => {
  if (!address) return <span className={className}>{children}</span>;
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  const handleClick = (e) => {
    // If a modifier key is pressed (cmd/ctrl/middle-click) the browser
    // already does the right thing — don't double-open the tab.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    e.stopPropagation();
    // `noopener` blanks `window.opener` so Google never gets a handle
    // back to our window. Falls back to plain target="_blank" if the
    // browser pop-up blocker refuses (rare for direct click handlers).
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (!w) {
      // Last-resort fallback: just navigate the current top frame.
      window.top.location.href = url;
    }
  };
  return (
    <a
      href={url}
      onClick={handleClick}
      target="_blank"
      rel="noopener noreferrer"
      className={`text-[#5B8DB8] hover:text-[#3a6a91] hover:underline ${className}`}
      data-testid={testid}
      title={`Open ${address} in Google Maps`}
    >
      {children ?? address}
    </a>
  );
};

export default MapsLink;
